/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { ClientRoles } from '@administration-client/user-shared';
import { generateValidationErrorId, InvalidParam } from '@common/tech-shared';
import { AsyncPipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { AbstractControl, FormControlStatus } from '@angular/forms';
import { ValidationErrorComponent } from '@ods/component';
import { isEmpty, uniqueId } from 'lodash-es';
import { filter, map, merge, Observable, of, take, tap } from 'rxjs';
import { UserFormService } from '../user.formservice';
import { RoleCheckboxComponent } from './role-checkbox/role-checkbox.component';

@Component({
  selector: 'administration-user-form-roles',
  imports: [ValidationErrorComponent, AsyncPipe, RoleCheckboxComponent],
  templateUrl: './user-form-roles.component.html',
})
export class UserFormRolesComponent implements OnInit {
  public readonly formService = inject(UserFormService);

  public invalidParams$: Observable<InvalidParam[]> = of([]);

  public readonly UserFormService = UserFormService;
  public readonly validationErrorId: string = generateValidationErrorId();
  public isValid: boolean = true;
  public readonly rolesHeadingId: string = uniqueId('rollen-ozg-heading-');

  ngOnInit(): void {
    this.setInvalidParams();
    this.loadAvailableRoles();
  }

  private setInvalidParams(): void {
    const control: AbstractControl = this.formService.form.controls[UserFormService.CLIENT_ROLES];

    const onSubmit$: Observable<InvalidParam[]> = this.formService.submitted$.pipe(
      map(() => (control.invalid ? Object.values(control.errors) : [])),
    );
    const onBecomesValid$: Observable<InvalidParam[]> = control.statusChanges.pipe(
      filter((status: FormControlStatus) => status === 'VALID'),
      map(() => []),
    );

    this.invalidParams$ = merge(onSubmit$, onBecomesValid$).pipe(
      tap((invalidParams: InvalidParam[]) => (this.isValid = isEmpty(invalidParams))),
    );
  }

  private loadAvailableRoles(): void {
    this.formService
      .getAvailableRoles()
      .pipe(take(1))
      .subscribe((clientRoles: ClientRoles): void => {
        this.formService.setAvailableRoles(UserFormService.ADMINISTRATION_GROUP, clientRoles.admin);
        this.formService.setAvailableRoles(UserFormService.ALFA_GROUP, clientRoles.alfa);
      });
  }

  public get adminRoles(): AbstractControl[] {
    return this.formService.getRoleArray(UserFormService.ADMINISTRATION_GROUP).controls;
  }

  public get alfaRoles(): AbstractControl[] {
    return this.formService.getRoleArray(UserFormService.ALFA_GROUP).controls;
  }
}
